/**
 * @file Form validation helpers.
 */

export type ValidationResult = {
  valid: boolean;
  message: string;
};

/**
 * Validates the expiration date format (MM/YY).
 *
 * @param value - The expiration date string to validate.
 * @returns A ValidationResult object with `valid` (boolean) and `message` (string).
 */
export const validateExpirationDate = (value: string): ValidationResult => {
  const expRegEx = /^(0[1-9]|1[0-2])\/([0-9]{2})$/;
  const exampleYear = `${new Date().getFullYear() + 3}`.slice(2);
  const exampleMonth = "01";

  return {
    valid: expRegEx.test(`${value}`.trim()),
    message: `Please enter a valid expiration date. E.g. ${exampleMonth}/${exampleYear}`
  };
};

/**
 * Validates if the given expiration date is not expired.
 *
 * @param value - The expiration date in the format "MM/YY".
 * @returns A ValidationResult object with `valid` (boolean) and `message` (string).
 */
export const validateIsNotExpired = (value: string): ValidationResult => {
  const [paddedMonth, twoDigitYear] = value.split ? value.split("/") : [];
  const month = paddedMonth.replace(/^0+/, "");
  const year = `20${twoDigitYear}`;

  return {
    valid: validateIsFutureDate(month, year).valid,
    message: `Please enter an expiration date in the future.`
  };
};

/**
 * Validates if the provided month and year represent a future date.
 *
 * @param month - The month as a string.
 * @param year - The year as a string.
 * @returns A ValidationResult object with `valid` (boolean) and `message` (string).
 */
export const validateIsFutureDate = (month: string, year: string): ValidationResult => {
  const monthNumeric = parseInt(month.toString(), 10);
  const yearNumeric = parseInt(year.toString(), 10);
  const comparisonMonthNumeric = new Date().getMonth() + 1; // adjust up one month due to zero-based months in JS
  const comparisonYearNumeric = new Date().getFullYear();

  return {
    valid:
      yearNumeric > comparisonYearNumeric ||
      (monthNumeric >= comparisonMonthNumeric && yearNumeric === comparisonYearNumeric),
    message: "Please enter a valid expiration date."
  };
};

/**
 * Validates if the given phone area code is valid. It must not start with a 0 or 1.
 *
 * @param value - The phone area code to validate.
 * @returns A ValidationResult object with `valid` (boolean) and `message` (string).
 */
export const validatePhoneAreacode = (value: string): ValidationResult => {
  const phoneRegEx = /^(?:[2-9][0-9]{2}|\([2-9][0-9]{2}\))/g;
  return {
    valid: phoneRegEx.test(value.trim()),
    message: "Please enter a valid area code. The area code must not start with a 0 or 1."
  };
};

/**
 * Validates a phone number string to ensure it matches the format of a 10-digit US phone number.
 * E.g.: `321-555-7890` or `(321) 555-7890` or `3215557890`.
 *
 * @param value - The phone number string to validate.
 * @returns A ValidationResult object with `valid` (boolean) and `message` (string).
 */
export const validatePhone = (value: string): ValidationResult => {
  const phoneRegEx = /^(?:[2-9][0-9]{2}|\([2-9][0-9]{2}\))\s?-?[0-9]{3}\s?-?[0-9]{4}$/g;
  return {
    valid: phoneRegEx.test(value.trim()),
    message: "Please enter a valid 10 digit phone number. E.g. (321) 555-7890."
  };
};

/**
 * Generates a validator function to check if a given date string (in MM/YY format)
 * is within a specified number of years from the current year.
 *
 * @param yearsOut - The number of years from the current year to validate against.
 * @returns A function that takes a date string (defaulting to an empty string) and
 *   returns a ValidationResult object with `valid` (boolean) and `message` (string).
 *
 * @example
 * const validator = generatePastYearsOutValidator(5);
 * const result = validator("12/25");
 * console.log(result.valid); // true or false
 * console.log(result.message); // "Please enter a valid expiration date within 5 years."
 */
export const generatePastYearsOutValidator =
  (yearsOut: number) =>
  (value = ""): ValidationResult => {
    const [paddedMonth, twoDigitYear] = value.split ? value.split("/") : ["", ""];
    const month = parseInt(paddedMonth.replace(/^0+/, ""));
    const year = parseInt(`20${twoDigitYear}`, 10);
    const compareYear = new Date().getFullYear() + yearsOut;
    const compareMonth = new Date().getMonth() + 1; // adjust up one month due to zero-based months in JS

    return {
      valid:
        !isNaN(month) &&
        year > 20 && // Year will be 20 here if the provided year is not a number
        (year <= compareYear || (month <= compareMonth && year === compareYear)),
      message: `Please enter a valid expiration date within ${yearsOut} years.`
    };
  };

/**
 * Validator to check if a date is not more than 10 years in the past.
 *
 * @returns A ValidationResult object with properties `valid` (boolean) and `message` (string).
 *
 * @constant
 * @type {Function}
 */
export const validateNotPast10Years = generatePastYearsOutValidator(10);

export default {
  validateIsNotExpired,
  validateNotPast10Years,
  validateExpirationDate,
  validatePhone
};
